// 텍스트 → Block 배열 — 순수 함수 (기획서 8.2.0, 13장 Worker 이전 대상)
// DOM·IndexedDB·전역 상태 접근 금지. 입력: NFC 정규화된 문자열.

// 한 block 최대 글자 수. 기획서 8.2.0: 기본 3000, 2000~4000 범위 튜닝값.
export const MAX_BLOCK_CHARS = 3000;

// 강제 분할 시 자를 위치를 찾는다. 가능하면 공백/문장부호 경계, 없으면 글자 수로.
function findCutPoint(text, from, hardLimit) {
  // [from, hardLimit) 구간 뒤쪽에서부터 경계 문자를 탐색
  const lookbackStart = Math.max(from, hardLimit - 200);
  for (let i = hardLimit - 1; i >= lookbackStart; i--) {
    const ch = text[i];
    if (ch === ' ' || ch === '\t' || ch === '.' || ch === '!' || ch === '?' ||
        ch === '。' || ch === '！' || ch === '？' || ch === '”' || ch === '"' ||
        ch === ')' || ch === '）' || ch === ',' || ch === '，') {
      return i + 1; // 경계 문자 다음에서 자름
    }
  }
  return hardLimit; // 경계 없음 → 글자 수로 강제 절단
}

/**
 * NFC 정규화된 전체 텍스트 → Block 배열.
 * 절차 (기획서 8.2.0):
 *  1차: 빈 줄(\n\n+) 기준 문단 분리. 단일 \n 은 문단 내 줄바꿈으로 유지.
 *  2차: MAX_BLOCK_CHARS 초과 문단 강제 세분화.
 *  각 block 에 누적 글자 인덱스로 start/end 부여.
 *
 * 반환 block: { index, start, end, text }
 *   start/end 는 "원본(파라그래프 분리 전) 정규화 텍스트 기준 글자 인덱스"가 아니라,
 *   화면에 실제로 렌더링되는 텍스트의 누적 인덱스다. 빈 줄 구분자는 텍스트에서 빠지므로
 *   globalOffset 의 totalChars 도 이 누적 기준을 따른다(아래 totalChars 참조).
 *
 * @param {string} normalizedText
 * @param {number} maxBlockChars
 * @returns {{ blocks: Array<{index:number,start:number,end:number,text:string}>, totalChars:number }}
 */
export function buildBlocks(normalizedText, maxBlockChars = MAX_BLOCK_CHARS) {
  const blocks = [];
  let cursor = 0; // 누적 글자 인덱스 (globalOffset 기준)

  // CRLF/CR 정규화
  const unified = normalizedText.replace(/\r\n?/g, '\n');

  // 1차 분할 — ★수정: "완전히 빈 줄"뿐 아니라 "공백/탭만 있는 줄"도
  //   문단 구분으로 인정한다. 일부 소설 TXT 는 단락 사이를 \n \n (공백 낀
  //   줄바꿈)로 구분하므로, \n{2,} 만으로는 4만 개 잔조각으로 깨진다.
  //   경계 정의: 줄바꿈 + (공백/탭만 있는 줄 0개 이상) + 줄바꿈.
  const rawParas = unified.split(/\n[ \t]*(?:\n[ \t]*)+/);

  for (let p = 0; p < rawParas.length; p++) {
    // 문단 내부에 남는 단일 줄바꿈은 한 칸 공백으로 정규화한다.
    // HTML 에서 \n 은 공백 하나로 무시되므로 그대로 두면 문단 경계가
    // 사라져 본문이 한 덩어리로 흘러 레이아웃이 깨진다(스샷 증상).
    // 좌표 일관성: 이 정규화 후의 길이를 globalOffset/totalChars 기준으로 삼는다.
    let para = rawParas[p].replace(/[ \t]*\n[ \t]*/g, ' ').trim();
    if (para.length === 0) continue;

    // 2차 강제 분할
    let offsetInPara = 0;
    while (offsetInPara < para.length) {
      const remaining = para.length - offsetInPara;
      let pieceLen;
      if (remaining <= maxBlockChars) {
        pieceLen = remaining;
      } else {
        const hardLimit = offsetInPara + maxBlockChars;
        const cut = findCutPoint(para, offsetInPara, hardLimit);
        pieceLen = cut - offsetInPara;
        if (pieceLen <= 0) pieceLen = maxBlockChars; // 안전장치
      }
      const pieceText = para.slice(offsetInPara, offsetInPara + pieceLen);
      blocks.push({
        index: blocks.length,
        start: cursor,
        end: cursor + pieceText.length,
        text: pieceText,
      });
      cursor += pieceText.length;
      offsetInPara += pieceLen;
    }
  }

  return { blocks, totalChars: cursor };
}

// globalOffset → blockIndex. blocks[].start 기준 이진 탐색 O(log n) (기획서 8.2.0).
export function findBlockByOffset(blocks, globalOffset) {
  if (blocks.length === 0) return -1;
  let lo = 0;
  let hi = blocks.length - 1;
  if (globalOffset <= blocks[0].start) return 0;
  if (globalOffset >= blocks[hi].end) return hi;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const b = blocks[mid];
    if (globalOffset < b.start) hi = mid - 1;
    else if (globalOffset >= b.end) lo = mid + 1;
    else return mid;
  }
  return Math.min(lo, blocks.length - 1);
}
